import React, { useState, useContext } from 'react';
import { View, ScrollView, StyleSheet, Alert } from 'react-native';
import { Text, Button, TextInput, Card, ProgressBar } from 'react-native-paper';
import { FirebaseTripsContext } from '../context/FirebaseTripsContext';
import { parseEmailContent } from '../services/emailParser';
import { Trip, DayItinerary } from '../types';
import { addDays } from '../utils/dateUtils';

export default function EmailImportScreen() {
  const [emailContent, setEmailContent] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedData, setParsedData] = useState<any>(null);
  const tripsContext = useContext(FirebaseTripsContext);

  const handleParse = () => {
    if (!emailContent.trim()) {
      Alert.alert('Error', 'Please paste email content first');
      return;
    }

    setIsProcessing(true);
    try {
      const activities = parseEmailContent(emailContent);
      
      if (activities.length === 0) {
        Alert.alert('No Results', 'Could not find any travel information in the email');
        setParsedData(null);
      } else {
        setParsedData({
          activities,
          suggestedName: 'Parsed Trip',
          suggestedDestination: 'Multiple Locations'
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to parse email content');
    } finally {
      setIsProcessing(false);
    }
  };

  const createTripFromParsed = async () => {
    if (!parsedData || !tripsContext) return;

    try {
      const startDate = new Date();
      const endDate = addDays(startDate, 3);
      
      const itinerary: DayItinerary[] = [{
        date: startDate,
        activities: parsedData.activities.map((activity: any, index: number) => ({
          id: `activity-${index}`,
          ...activity
        }))
      }];

      const newTrip: Omit<Trip, 'id' | 'createdAt' | 'updatedAt'> = {
        name: parsedData.suggestedName,
        destination: parsedData.suggestedDestination,
        startDate,
        endDate,
        itinerary,
        collaborators: []
      };

      await tripsContext.createTrip(newTrip);
      Alert.alert('Success', 'Trip created from email!');
      setEmailContent('');
      setParsedData(null);
    } catch (error) {
      Alert.alert('Error', 'Failed to create trip');
    }
  };

  const connectGmail = () => {
    Alert.alert(
      'Connect Gmail',
      'Gmail integration will automatically scan your inbox for travel confirmations. This feature is coming soon!',
      [{ text: 'OK' }]
    );
  };

  const connectOutlook = () => {
    Alert.alert(
      'Connect Outlook',
      'Outlook integration will automatically scan your inbox for travel confirmations. This feature is coming soon!',
      [{ text: 'OK' }]
    );
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        <Text variant="headlineMedium" style={styles.title}>
          Import from Email
        </Text>
        
        <Card style={styles.card}>
          <Card.Content>
            <Text variant="titleMedium" style={styles.cardTitle}>
              Connect Email Account
            </Text>
            <Text style={styles.description}>
              Connect your email to automatically import travel confirmations
            </Text>
            <Button
              icon="gmail"
              mode="contained"
              style={styles.button}
              onPress={connectGmail}
            >
              Connect Gmail
            </Button>
            <Button
              icon="microsoft-outlook"
              mode="outlined"
              style={styles.button}
              onPress={connectOutlook}
            >
              Connect Outlook
            </Button>
          </Card.Content>
        </Card>

        <Card style={styles.card}>
          <Card.Content>
            <Text variant="titleMedium" style={styles.cardTitle}>
              Manual Email Import
            </Text>
            <Text style={styles.description}>
              Paste your travel confirmation email below
            </Text>
            
            <TextInput
              label="Email Content"
              value={emailContent}
              onChangeText={setEmailContent}
              mode="outlined"
              multiline
              numberOfLines={10}
              style={styles.textInput}
            />
            
            {isProcessing && <ProgressBar indeterminate style={styles.progress} />}
            
            <Button
              mode="contained"
              onPress={handleParse}
              disabled={isProcessing || !emailContent.trim()}
              style={styles.button}
            >
              Parse Email
            </Button>

            {parsedData && (
              <View style={styles.results}>
                <Text variant="titleSmall">Found {parsedData.activities.length} activities</Text>
                <Button
                  mode="contained"
                  onPress={createTripFromParsed}
                  style={styles.button}
                >
                  Create Trip
                </Button>
              </View>
            )}
          </Card.Content>
        </Card>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    padding: 16,
  },
  title: {
    marginBottom: 24,
    textAlign: 'center',
  },
  card: {
    marginBottom: 16,
  },
  cardTitle: {
    marginBottom: 8,
  },
  description: {
    marginBottom: 16,
    color: '#666',
  },
  button: {
    marginTop: 8,
  },
  textInput: {
    marginBottom: 16,
    backgroundColor: 'white',
  },
  progress: {
    marginBottom: 16,
  },
  results: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
  },
});